/**
 * Non-conformités (QUAL-001) — requêtes fiche/board/actions liées + mutations
 * (création, autosave dont les notes libres, transition de statut, scrubbing).
 *
 * Le REGISTRE (liste) est servi par `<DataTable mode="server">` (clé de cache
 * `[endpoint, params]`) : ne PAS écrire de hook de liste ici. Après mutation on
 * invalide la liste DataTable (`invalidateDataTableListings`) ET la clé fiche.
 */

import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import type { BulkImpact } from '@/components/ui/data-table/actions/action-types'
import { API_BASE, apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { toBulkImpact, type BulkImpactPayload } from '@/lib/bulk-impact'
import { invalidateDataTableListings } from '@/lib/datatable-invalidate'
import { queryKeys } from '@/lib/queryKeys'

import type {
    ActionListResponse,
    NCStatus,
    NCStatusTransition,
    NonConformityCreate,
    NonConformityDetail,
    NonConformityUpdate,
    QualityBulkResult,
} from './useQuality.types'

const NC_ENDPOINT = `${API_BASE}/quality/non-conformities`
/** Chemin exact passé en prop `endpoint` du DataTable (clé de cache liste). */
export const NC_LISTING_ENDPOINT = '/api/quality/non-conformities'

function headers(entityId: string | null | undefined): HeadersInit {
    const h: Record<string, string> = { 'content-type': 'application/json' }
    if (entityId) h['x-entity-id'] = entityId
    return h
}

/** Invalide la liste DataTable + la fiche + les actions liées après mutation. */
function invalidateNC(qc: QueryClient, ncId?: string) {
    invalidateDataTableListings(qc, NC_LISTING_ENDPOINT, queryKeys.nonConformities.all)
    // ⚠️ `refetchType: 'all'` — sans lui (défaut `'active'`), une fiche DÉMONTÉE
    // est seulement marquée périmée : on revient dessus après une action de masse
    // et l'ancienne valeur s'affiche une fraction de seconde avant le refetch au
    // montage. L'utilisateur croit que son action n'a pas pris.
    if (ncId) {
        void qc.invalidateQueries({
            queryKey: queryKeys.nonConformities.detail(ncId),
            refetchType: 'all',
        })
        void qc.invalidateQueries({
            queryKey: queryKeys.nonConformities.actions(ncId),
            refetchType: 'all',
        })
    }
}

// ── Requêtes ────────────────────────────────────────────────────────────────
export function useNonConformity(
    ncId: string | null | undefined,
    entityId: string | null | undefined,
) {
    return useQuery<NonConformityDetail>({
        queryKey: queryKeys.nonConformities.detail(ncId ?? ''),
        queryFn: async () => {
            const url = `${NC_ENDPOINT}/${ncId}`
            const response = await apiFetch(url, { headers: headers(entityId) })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!ncId && !!entityId,
    })
}

// NB : le board Kanban NC ne passe plus par une requête plate bornée à 100 fiches
// (troncature silencieuse) : il pagine PAR COLONNE via `useNCBoardColumn`
// (`hooks/useQualityBoards.ts`), avec les compteurs d'en-tête servis par les facettes.

/** Actions correctives/préventives liées (surface partagée avec QUAL-002). */
export function useNCLinkedActions(
    ncId: string | null | undefined,
    entityId: string | null | undefined,
) {
    return useQuery<ActionListResponse>({
        queryKey: queryKeys.nonConformities.actions(ncId ?? ''),
        queryFn: async () => {
            const url = `${NC_ENDPOINT}/${ncId}/actions`
            const response = await apiFetch(url, { headers: headers(entityId) })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!ncId && !!entityId,
    })
}

// ── Mutations ───────────────────────────────────────────────────────────────
export function useCreateNonConformity(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<NonConformityDetail, Error, NonConformityCreate>({
        mutationFn: async (body) => {
            const response = await apiFetch(NC_ENDPOINT, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify(body),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: (created) => invalidateNC(qc, created.id),
    })
}

export function useUpdateNonConformity(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<NonConformityDetail, Error, { id: string; patch: NonConformityUpdate }>({
        mutationFn: async ({ id, patch }) => {
            const response = await apiFetch(`${NC_ENDPOINT}/${id}`, {
                method: 'PATCH',
                headers: headers(entityId),
                body: JSON.stringify(patch),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: (updated) => invalidateNC(qc, updated.id),
    })
}

export function useTransitionNonConformity(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<NonConformityDetail, Error, { id: string; body: NCStatusTransition }>({
        mutationFn: async ({ id, body }) => {
            const response = await apiFetch(`${NC_ENDPOINT}/${id}/status`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify(body),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: (updated) => invalidateNC(qc, updated.id),
    })
}

export function useAnonymizeNonConformity(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<void, Error, string>({
        mutationFn: async (id) => {
            const response = await apiFetch(`${NC_ENDPOINT}/${id}/anonymize`, {
                method: 'POST',
                headers: headers(entityId),
            })
            if (!response.ok) await handleApiError(response)
        },
        onSuccess: (_v, id) => invalidateNC(qc, id),
    })
}

/** Action de masse (§32_TABLE §D). `action` = 'delete' (scrubbing) | 'set_status'
 *  (changement de statut en masse, `new_status` intermédiaire requis ; la clôture
 *  n'est pas disponible en masse). */
export function useBulkNonConformities(entityId: string | null | undefined) {
    const qc = useQueryClient()
    return useMutation<
        QualityBulkResult,
        Error,
        { ids: string[]; action: string; new_status?: NCStatus }
    >({
        mutationFn: async ({ ids, action, new_status }) => {
            const response = await apiFetch(`${NC_ENDPOINT}/bulk`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify({ ids, action, new_status }),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        onSuccess: () => invalidateNC(qc),
    })
}

/**
 * Préfixe des clés i18n des motifs d'exclusion du registre des NC.
 *
 * Le serveur ne renvoie qu'une CLÉ stable (`INHERITED`) : la phrase vit ici, en
 * FR et en EN. Un motif renommé côté serveur casserait la traduction — on ajoute
 * des motifs, on n'en renomme pas.
 *
 * **Sans point final** : `toBulkImpact` le pose.
 */
export const NC_BULK_REASON_PREFIX = 'quality.nc.bulk.reason'

/**
 * Aperçu d'impact d'une action de masse sur les non-conformités.
 *
 * Appelle la route de masse EXISTANTE en `?dry_run=true`, donc la MÊME fonction
 * d'éligibilité que l'exécution : le nombre annoncé est celui qui sera appliqué
 * (convention `.agent/conventions/bulk-action-impact-preview.md`). Aucune
 * écriture, aucune trace d'audit côté serveur.
 *
 * Rendu comme un `useCallback` et non comme une mutation : l'aperçu part à
 * l'ouverture du dialogue de confirmation et son état est piloté par le socle
 * (`useBulkImpact`), pas par TanStack Query.
 */
export function useNCBulkImpact(entityId: string | null | undefined) {
    return useCallback(
        async (
            action: string,
            ids: string[],
            newStatus?: NCStatus,
        ): Promise<BulkImpact> => {
            const response = await apiFetch(`${NC_ENDPOINT}/bulk?dry_run=true`, {
                method: 'POST',
                headers: headers(entityId),
                body: JSON.stringify({ ids, action, new_status: newStatus }),
            })
            if (!response.ok) await handleApiError(response)
            return toBulkImpact(
                (await response.json()) as BulkImpactPayload,
                NC_BULK_REASON_PREFIX,
            )
        },
        [entityId],
    )
}
